/**
 * Media - the host site's library: browse, search, upload, delete, folders.
 *
 * A focused version of web_admin's MediaPage (no stock, canva, video or bulk).
 * The API is site-scoped, so every call carries `X-Site-Id` with the host site's
 * id (`lib/media`).
 *
 * The screen arrived here with the shell already redesigned and the contents
 * not: a hand-written `<h1>`, a bordered search box, a `Loader`, an empty state
 * that was a paragraph, and a list view that was still a `<table>`. It draws
 * from the CRM's own vocabulary now, same as every other section. The tiles and
 * the rows are two views of one library and are kept in `MediaGrid` and
 * `MediaList`; what stays here is the page: what is loaded, which folder is
 * open, and what upload and delete do.
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Images, LayoutGrid, List, Loader2, Upload } from "lucide-react";
import { cn } from "@wa/ui";
import { useT } from "../../../i18n/I18nProvider";
import { hostSiteId, type StaffUser } from "../../../lib/auth";
import {
  createFolder, deleteAsset, listFolders, listMedia, uploadFile, type MediaAsset,
} from "../../../lib/media";
import { GridZoomControl, readStoredZoom } from "../../../components/GridZoomControl";
import { Btn, EmptyState, PageHead, PillTabs, SearchField, Toolbar, ToolbarGap } from "../../ui";
import { PopHost } from "../../shell/Pop";
import { ConfirmModal } from "../_shared/ConfirmModal";
import { FolderRail } from "./FolderRail";
import { MediaGrid } from "./MediaGrid";
import { MediaList } from "./MediaList";

type View = "grid" | "list";

const VIEW_KEY = "crm.media.view";
const TILE_KEY = "crm.media.tile";

export function MediaSection({ user }: { user: StaffUser }) {
  const t = useT();
  const siteId = hostSiteId(user);

  const [items, setItems] = useState<MediaAsset[]>([]);
  const [folders, setFolders] = useState<string[]>([]);
  /** "" is every folder, which is the view the screen opens on. */
  const [activeFolder, setActiveFolder] = useState<string>("");
  const [search, setSearch] = useState("");
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [confirmDel, setConfirmDel] = useState<MediaAsset | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInput = useRef<HTMLInputElement>(null);

  // Grid or rows, remembered - an operator who works in one of them works in it
  // every day, and re-picking it on every visit is a small daily tax.
  const [view, setView] = useState<View>(() =>
    typeof window !== "undefined" && window.localStorage.getItem(VIEW_KEY) === "list" ? "list" : "grid",
  );
  useEffect(() => {
    window.localStorage.setItem(VIEW_KEY, view);
  }, [view]);

  // Tile size in pixels, also remembered. Written on a delay because the zoom
  // control can be held down and fires on every frame while it is.
  const [tile, setTile] = useState<number>(() => readStoredZoom(TILE_KEY, 170));
  useEffect(() => {
    const id = setTimeout(() => window.localStorage.setItem(TILE_KEY, String(tile)), 200);
    return () => clearTimeout(id);
  }, [tile]);

  const refreshFolders = useCallback(() => {
    if (!siteId) return;
    listFolders(siteId).then(setFolders).catch(() => setFolders([]));
  }, [siteId]);

  const fetchItems = useCallback(
    async (term: string, folder: string) => {
      if (!siteId) return;
      setLoading(true);
      try {
        const res = await listMedia(siteId, { search: term || undefined, folder: folder || undefined, limit: 100 });
        setItems(res.items);
      } catch {
        setItems([]);
      } finally {
        setLoading(false);
      }
    },
    [siteId],
  );

  useEffect(() => {
    refreshFolders();
  }, [refreshFolders]);

  useEffect(() => {
    const id = setTimeout(() => void fetchItems(search, activeFolder), search ? 300 : 0);
    return () => clearTimeout(id);
  }, [search, activeFolder, fetchItems]);

  async function handleFiles(files: FileList | File[]) {
    if (!siteId) return;
    const list = Array.from(files);
    if (!list.length) return;
    setUploading(true);
    try {
      for (const f of list) await uploadFile(siteId, f, activeFolder || "/");
      await fetchItems(search, activeFolder);
    } finally {
      setUploading(false);
    }
  }

  async function addFolder(name: string) {
    if (!siteId) return;
    await createFolder(siteId, name);
    refreshFolders();
    setActiveFolder(`/${name.replace(/^\/+/, "").replace(/\/+$/, "")}`);
  }

  async function doDelete(asset: MediaAsset) {
    if (!siteId) return;
    await deleteAsset(siteId, asset.id);
    setItems((prev) => prev.filter((i) => i.id !== asset.id));
  }

  if (!siteId) {
    return <EmptyState icon={Images} title={t("media.title")} body={t("media.noSite")} />;
  }

  const folderLabel = (f: string) => (f === "/" ? t("media.rootFolder") : f.replace(/^\//, ""));
  // "Nothing here" and "nothing matches what you asked for" are different facts,
  // and only one of them means the library is empty.
  const narrowed = search.trim() !== "" || activeFolder !== "";

  return (
    // One open menu at a time across every tile and row.
    <PopHost>
      <PageHead title={t("media.title")} sub={t("media.count", { n: items.length })} />

      <Toolbar>
        <SearchField value={search} onChange={setSearch} placeholder={t("media.search")} />
        <ToolbarGap />
        {view === "grid" ? <GridZoomControl value={tile} onChange={setTile} slider /> : null}
        {/* Grid or rows is a shape, not a subject: the two glyphs say it faster
            than the two words did, and they are the same pair the media picker
            uses. The wording lives on in the tooltip and the accessible name. */}
        <PillTabs
          value={view}
          onChange={setView}
          iconOnly
          tabs={[
            { value: "grid" as View, label: t("media.viewGrid"), icon: <LayoutGrid className="size-4" /> },
            { value: "list" as View, label: t("media.viewList"), icon: <List className="size-4" /> },
          ]}
        />
        <input
          ref={fileInput}
          type="file"
          multiple
          accept="image/*"
          className="hidden"
          onChange={(e) => e.target.files && void handleFiles(e.target.files)}
        />
        <Btn tone="brand" onClick={() => fileInput.current?.click()} disabled={uploading}>
          {uploading ? <Loader2 className="size-4 animate-spin" /> : <Upload className="size-4" />}
          {t("media.upload")}
        </Btn>
      </Toolbar>

      <div className="grid gap-5 lg:grid-cols-[200px_1fr]">
        <FolderRail
          folders={folders}
          active={activeFolder}
          onSelect={setActiveFolder}
          onCreate={addFolder}
          folderLabel={folderLabel}
        />

        <div
          onDragOver={(e) => {
            e.preventDefault();
            setDragOver(true);
          }}
          onDragLeave={() => setDragOver(false)}
          onDrop={(e) => {
            e.preventDefault();
            setDragOver(false);
            if (e.dataTransfer.files.length) void handleFiles(e.dataTransfer.files);
          }}
          className={cn("crm-card min-h-[300px]", dragOver && "ring-2 ring-primary/60")}
        >
          {loading ? (
            <div className="flex justify-center py-20 text-muted-foreground">
              <Loader2 className="size-6 animate-spin" />
            </div>
          ) : items.length === 0 ? (
            <EmptyState
              icon={Images}
              title={t(narrowed ? "media.noMatch" : "media.empty")}
              body={t(narrowed ? "media.noMatchBody" : "media.emptyHint")}
              action={
                narrowed ? null : (
                  <Btn tone="brand" onClick={() => fileInput.current?.click()}>
                    <Upload className="size-4" />
                    {t("media.upload")}
                  </Btn>
                )
              }
            />
          ) : view === "list" ? (
            <MediaList items={items} folderLabel={folderLabel} onDelete={setConfirmDel} />
          ) : (
            <MediaGrid items={items} tile={tile} onDelete={setConfirmDel} />
          )}
        </div>
      </div>

      {confirmDel ? (
        <ConfirmModal
          title={t("media.deleteConfirm")}
          body={confirmDel.originalFilename}
          confirmLabel={t("media.delete")}
          cancelLabel={t("media.cancel")}
          onConfirm={async () => {
            const asset = confirmDel;
            setConfirmDel(null);
            await doDelete(asset);
          }}
          onClose={() => setConfirmDel(null)}
        />
      ) : null}
    </PopHost>
  );
}
